import { useEffect, useRef, useState } from 'react';
import { Link } from 'wouter';
import { useRuns } from '../lib/pipeline';
import { useDrafts } from '../lib/drafts';

interface Toast {
  runId: string;
  pipeline: 'setup' | 'runtime';
  status: 'completed' | 'failed';
  href: string;
  headline: string;
  helper?: string;
}

/**
 * Ambient run-completion toast. Watches the project's runs list and fires a
 * floating notification when a previously-running run transitions to
 * completed/failed. Lets the operator wander to other pages without losing
 * the handoff.
 *
 * One toast at a time; click to navigate, dismiss button, or auto-dismiss
 * after 8 seconds.
 */
export function RunCompleteToast({ projectId }: { projectId: string }) {
  const runs = useRuns(projectId);
  const drafts = useDrafts(projectId, 'all');
  const seenIds = useRef<Set<string>>(new Set());
  const seededRef = useRef(false);
  const [toast, setToast] = useState<Toast | null>(null);

  useEffect(() => {
    if (!runs.data) return;

    // First load: mark every existing non-running run as already-seen so we
    // don't fire toasts for runs that finished before the operator opened
    // this tab.
    if (!seededRef.current) {
      runs.data.runs.forEach((r) => {
        if (r.status !== 'running') seenIds.current.add(r.id);
      });
      seededRef.current = true;
      return;
    }

    for (const run of runs.data.runs) {
      if (run.status === 'running') continue;
      if (seenIds.current.has(run.id)) continue;
      seenIds.current.add(run.id);

      const isFail = run.status === 'failed';
      const isSetup = run.pipeline === 'setup';

      if (isFail) {
        setToast({
          runId: run.id,
          pipeline: run.pipeline,
          status: 'failed',
          href: `/projects/${projectId}/${isSetup ? 'setup' : 'generator'}`,
          headline: `Your ${isSetup ? 'strategy' : 'content'} team hit a snag.`,
          helper: run.error?.slice(0, 120),
        });
      } else if (isSetup) {
        setToast({
          runId: run.id,
          pipeline: 'setup',
          status: 'completed',
          href: `/projects/${projectId}/setup`,
          headline: 'Your strategy team finished.',
          helper: 'Open the team to curate candidates.',
        });
      } else {
        const newestDraft = drafts.data?.[0];
        const href = newestDraft
          ? `/projects/${projectId}/posts/${newestDraft.id}`
          : `/projects/${projectId}/posts`;
        setToast({
          runId: run.id,
          pipeline: 'runtime',
          status: 'completed',
          href,
          headline: 'Your content team just finished.',
          helper: 'Draft ready for your review.',
        });
      }
      // Show the most recent completion only; further loop iterations would
      // overwrite anyway.
      break;
    }
  }, [runs.data, drafts.data, projectId]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 8000);
    return () => clearTimeout(t);
  }, [toast]);

  if (!toast) return null;

  const isFail = toast.status === 'failed';
  const accent = isFail ? 'border-accent-amber/60' : 'border-accent-emerald/60';
  const iconColour = isFail ? 'text-accent-amber' : 'text-accent-emerald';
  const icon = isFail ? 'warning' : 'task_alt';

  return (
    <div className="fixed bottom-6 right-6 z-50 max-w-sm pointer-events-none">
      <Link
        href={toast.href}
        onClick={() => setToast(null)}
        className={`pointer-events-auto flex items-start gap-3 rounded-xl border ${accent} bg-surface px-4 py-3 shadow-2xl shadow-black/40 hover:bg-surface-2 transition-colors`}
      >
        <span className={`material-symbols-outlined ${iconColour} text-[22px] shrink-0`}>{icon}</span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-sm font-bold text-text-primary">{toast.headline}</p>
          {toast.helper && <p className="text-xs text-muted mt-0.5 leading-relaxed">{toast.helper}</p>}
        </div>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setToast(null);
          }}
          className="material-symbols-outlined text-muted hover:text-text-primary text-[16px] -mt-1 shrink-0"
          aria-label="Dismiss"
        >
          close
        </button>
      </Link>
    </div>
  );
}
